/**
 * Which Backend this build talks to, decided from the hostname it is served from.
 *
 * THE PILOT HAZARD
 * ---------------------------------------------------------------
 * The Pilot is hosted at `pilot.jindamarket.com` and must reach the
 * STAGING API. The previous rule was
 *
 *     } else if (h.indexOf("jinda.business") >= 0 || h.indexOf("jindamarket.com") >= 0) {
 *         host = PRODUCTION;
 *
 * and `"pilot.jindamarket.com".indexOf("jindamarket.com")` is 6, not -1 --
 * the Pilot subdomain CONTAINS the production domain, so the Pilot would
 * have driven the real shop's Production database. The Pilot host is
 * therefore matched FIRST, and by exact hostname rather than substring:
 * `pilot.jindamarket.com.example.net` must never be read as the Pilot
 * either.
 *
 * WHAT DID NOT CHANGE
 * ---------------------------------------------------------------
 * Every other branch keeps its exact previous behaviour, including the
 * order it was evaluated in:
 *
 *   localhost / 127.0.0.1 / 192.168.* / 10.* / 172.* / port 3000  -> Staging
 *   jinda.business / jindamarket.com                              -> Production
 *   anything else (unknown public host)                           -> Production
 *
 * The last line is the fail-safe and is deliberately left as it was: an
 * unrecognised host is NOT quietly treated as a Pilot or a development
 * machine. Only the one hostname the Pilot is actually served from gets
 * the Staging API.
 */

export const PRODUCTION_API = 'https://epx7wx2ggf.execute-api.ap-southeast-1.amazonaws.com';
export const STAGING_API = 'https://jfdsg8smu0.execute-api.ap-southeast-1.amazonaws.com';

/** The one hostname the Pilot is served from. */
export const PILOT_HOSTNAME = 'pilot.jindamarket.com';

/**
 * `hostname` is `window.location.hostname` (no port), `host` may carry the
 * port, and `port` is `window.location.port`. Kept as three arguments
 * because the original rule mixed all three, and this must reproduce it.
 */
export const resolveApiHost = (hostname: string, host: string, port: string): string => {
    const hn = hostname || '';
    const h = host || '';
    const p = port || '';

    // FIRST, and by exact match: the Pilot subdomain would otherwise be
    // caught by the `jindamarket.com` substring test below.
    if (hn === PILOT_HOSTNAME) {
        return STAGING_API;
    }

    if (
        h.indexOf('localhost') >= 0 ||
        h.indexOf('127.0.0.1') >= 0 ||
        hn === 'localhost' ||
        hn === '127.0.0.1' ||
        hn.startsWith('192.168.') ||
        hn.startsWith('10.') ||
        hn.startsWith('172.') ||
        p === '3000'
    ) {
        return STAGING_API;
    }

    if (h.indexOf('jinda.business') >= 0 || h.indexOf('jindamarket.com') >= 0) {
        return PRODUCTION_API;
    }

    return PRODUCTION_API;
};

/** True only on the Pilot host -- used to badge the UI. */
export const isPilotHost = (hostname?: string): boolean => {
    if (typeof hostname === 'string') {
        return hostname === PILOT_HOSTNAME;
    }
    if (typeof window === 'undefined') {
        return false;
    }
    return (window.location.hostname || '') === PILOT_HOSTNAME;
};

let host = PRODUCTION_API;

if (typeof window !== 'undefined') {
    host = resolveApiHost(
        window.location.hostname || '',
        window.location.host || '',
        window.location.port || '',
    );
}

export const base_url: string = host;

/**
 * True when this build is talking to the real shop's Backend.
 *
 * Used to keep development affordances -- anything that previews or
 * pretends -- off the live system. It asks the question that actually
 * matters ("whose data is behind this screen?") rather than guessing from
 * the hostname: localhost, a LAN address and the Pilot all answer `false`
 * here because all three point at Staging, and a future host added to
 * `resolveApiHost` inherits the right answer without anyone remembering
 * to update a second list.
 */
export const isProductionApi = (url: string = base_url): boolean =>
    url === PRODUCTION_API;
